/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { TableInfoModel, TableInfoRepository, TableTypeEnum, WorkspaceRepository } from '@database/central';
import {
	ColumnRepository,
	DataRepository,
	ReferenceTableRepository,
	TableRepository,
	WorkspaceDatabaseKeys
} from '@database/workspace';
import { QueryParamsProvider } from '@domain/routing';
import { combineLatest, Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { ExportJSONFileModel } from './models/export-file.model';
import { DataModel, formatDateToString } from '@shared';
import { BusinessLayerService } from '../business-layer/services/business-layer.service';
import { ExportSanitizer } from './sanitizers/export.sanitizier';
import { ApiActionTypeEnum } from '../business-layer/entity';
import { flatten } from 'lodash';
import { PrimaryKeyService } from '../business-layer/services/primary-key.service';
import { ParentRecordPrimaryKeyValuesModel } from '../business-layer/models/parent-record-primary-key-values.model';
//todo: put this in providers at component level
@Injectable()
export class FileExporterDataProvider {
	public constructor(
		private workspaceRepository: WorkspaceRepository,
		private tableRepository: TableRepository,
		private queryParamsProvider: QueryParamsProvider,
		private dataRepository: DataRepository,
		private columnRepository: ColumnRepository,
		private tableInfoRepository: TableInfoRepository,
		private refTablesRepository: ReferenceTableRepository,
		private businessLayerService: BusinessLayerService,
		private exportSanitizer: ExportSanitizer,
		private primaryKeyService: PrimaryKeyService
	) {}

	public export$(workfileId: string): Observable<ExportJSONFileModel> {
		console.log(workfileId, "ini export");
		return this.tableInfoRepository.getAll$(workfileId).pipe(
			switchMap((tableInfos: TableInfoModel[]) =>
				this.getJsonContents$(tableInfos).pipe(
					switchMap((fileModel: ExportJSONFileModel) => of(this.exportSanitizer.sanitize(fileModel)))
				)
			),
			first()
		);
	}

	public exportCopyInCheckIn$(
		workfileId: string,
		tableName: string,
		entryId: number,
		apiActionTypeEnum: ApiActionTypeEnum
	): Observable<ExportJSONFileModel> {
		return this.tableInfoRepository.getAll$(workfileId).pipe(
			switchMap((tableInfos: TableInfoModel[]) =>
				this.getCopyInCheckInJsonContents$(tableInfos, tableName, entryId, apiActionTypeEnum).pipe(
					switchMap((fileModel: ExportJSONFileModel) => of(this.exportSanitizer.sanitize(fileModel)))
				)
			),
			first()
		);
	}

	public exportCheckOut$(): Observable<ExportJSONFileModel> {
		return this.getCheckOutJsonContents$().pipe(
			switchMap((fileModel: ExportJSONFileModel) => of(this.exportSanitizer.sanitize(fileModel))),
			first()
		);
	}

	public getDataFromAllTables$(dataTableQueries: Observable<any>[]): Observable<DataModel[][]> {
		return combineLatest(dataTableQueries);
	}

	public getFusionData$(dataTableQueries: any[]) {
		return this.getDataFromAllTables$(dataTableQueries).pipe(
			switchMap((result: DataModel[][]) => {
				const data: DataModel[] = flatten(result);

				// get deleted parent records (soft delete)
				const softDeletedRecords = data.filter((recordDetails: DataModel) => recordDetails.isDeleted);
				// unique deleted parent table names
				// ['DRILL_HOLE', 'SAMPLE_STATION', 'UDEF_CHIPS']
				const softDeletedTablesName = [
					...new Set(
						softDeletedRecords.map(
							(recordDetails: DataModel) => recordDetails[WorkspaceDatabaseKeys.columns.tableName]
						)
					)
				];

				if (softDeletedRecords.length === 0) {
					return of(data);
				}

				return this.businessLayerService
					.getPrimaryKeys$(this.queryParamsProvider.workfileId, softDeletedTablesName)
					.pipe(
						// unique deleted table names with primary key
						// {"DRILL_HOLE": "hole_number", "SAMPLE_STATION": "sample_number", "UDEF_CHIPS": "chip_name"}
						map((softDeletedTablesPrimaryKey: { [key: string]: string[] }) => {
							// build an array of properties and values for skipping (soft deleted parent and children records)
							// [{"primaryKey": ["hole_number"], "primaryKeyValue": ["DM_2017-Demo-test"],
							// "projectNumberKey": "project_number", "projectNumberValue": "DM_2017"}]
							const softDeletedTablesPrimaryKeyValues = softDeletedRecords.map(
								(deletedRecord: DataModel) => {
									const recordPrimaryKey: string[] =
										softDeletedTablesPrimaryKey[
											deletedRecord[WorkspaceDatabaseKeys.columns.tableName]
										];

									const recordPrimaryKeyValue = recordPrimaryKey.map(
										(primaryKey: string) => deletedRecord[primaryKey]
									);

									return {
										primaryKey: recordPrimaryKey,
										primaryKeyValue: recordPrimaryKeyValue,
										projectNumberKey: WorkspaceDatabaseKeys.columns.projectNumber,
										projectNumberValue: deletedRecord.project_number
									};
								}
							);

							return data.reduce((acc: DataModel[], curr: DataModel) => {
								// Keep only those records that don't match all conditions
								// 1. primary key(s) name(s)
								// 2. primary key(s) value(s)
								// 3. project number
								// 4. project value
								if (
									!softDeletedTablesPrimaryKeyValues.some(
										(propToCompare) =>
											propToCompare.primaryKey.every((pk: string) => pk in curr) &&
											propToCompare.primaryKeyValue.every(
												(pkValue: string | number, i: number) =>
													pkValue === curr[propToCompare.primaryKey[i]]
											) &&
											propToCompare.projectNumberKey in curr &&
											propToCompare.projectNumberValue === curr[propToCompare.projectNumberKey]
									)
								) {
									return acc.concat(curr);
								}

								return acc;
							}, []);
						})
					);
			})
		);
	}

	/* eslint-disable @typescript-eslint/no-unused-vars */
	public getFusionParentChildrensData$(
		dataTableQueries: any[],
		tableName: string,
		entryId: number,
		apiActionTypeEnum: ApiActionTypeEnum
	) {
		return this.getDataFromAllTables$(dataTableQueries).pipe(
			switchMap((result: DataModel[][]) => {
				const data: DataModel[] = flatten(result);

				const parentRecord: DataModel = data.find(
					(item: DataModel) => item.table_name === tableName && item.rxdbid === entryId
				);

				return this.businessLayerService.getPrimaryKey$(this.queryParamsProvider.workfileId, tableName).pipe(
					map((tablePrimaryKeys: string[]) => {
						const primaryKeyValues = this.primaryKeyService.parentChildrensRecords(
							tablePrimaryKeys,
							parentRecord
						);

						const parentAndChildTableRecords: DataModel[] = [];
						data.forEach((item: DataModel) => {
							if (this.isChildRecordOf(primaryKeyValues, item)) {
								parentAndChildTableRecords.push(item);
							}
						});

						return parentAndChildTableRecords;
					})
				);
			})
		);
	}

	private getJsonContents$(tableInfos: TableInfoModel[]) {
		const { dataTableQueries, refTableQueries } = this.getQueries(tableInfos);

		const fusionData$ = this.getFusionData$(dataTableQueries);
		const fusionRefTables$ = this.getFusionRefTables$(refTableQueries);
		const fusionEntities$ = this.workspaceRepository.getEntitiesById$(this.queryParamsProvider.workfileId);
		const fusionDataTables$ = this.tableRepository.getTables$();
		const columns$ = this.columnRepository.getAllColumns$();
		const users$ = this.workspaceRepository.getUsers$(this.queryParamsProvider.workfileId);
		const configHeaders$ = this.workspaceRepository.getConfigHeaders$(this.queryParamsProvider.workfileId);
		const workfileName$ = this.workspaceRepository.getWorkFileNameById$(this.queryParamsProvider.workfileId);

		return combineLatest([
			fusionEntities$,
			fusionDataTables$,
			columns$,
			fusionData$,
			fusionRefTables$,
			users$,
			configHeaders$,
			workfileName$
		]).pipe(
			map((result) => {
				const fileContents: ExportJSONFileModel = {
					FusionEntities: result[0],
					FusionDataTables: result[1],
					FusionDataColumns: result[2],
					TABLE_DATA: result[3],
					FusionReferenceTables: result[4],
					FusionUsers: result[5],
					FusionConfigHeader: result[6],
					FileName: this.generateFileName(result[7])
				};

				return fileContents;
			})
		);
	}

	private getCopyInCheckInJsonContents$(
		tableInfos: TableInfoModel[],
		tableName: string,
		entryId: number,
		apiActionTypeEnum: ApiActionTypeEnum
	) {
		const { dataTableQueries } = this.getQueries(tableInfos);

		const configHeaders$ = this.workspaceRepository.getConfigHeaders$(this.queryParamsProvider.workfileId);
		const fusionData$ = this.getFusionParentChildrensData$(dataTableQueries, tableName, entryId, apiActionTypeEnum);
		const workfileName$ = this.workspaceRepository.getWorkFileNameById$(this.queryParamsProvider.workfileId);

		return combineLatest([configHeaders$, fusionData$, workfileName$]).pipe(
			map(([configHeaders, tableData, workfileName]) => {
				const fileContents: ExportJSONFileModel = {
					FusionConfigHeader: configHeaders,
					FusionEntities: [],
					FusionDataTables: [],
					FusionDataColumns: [],
					FusionReferenceTables: [],
					FusionUsers: [],
					TABLE_DATA: tableData,
					FileName: this.generateFileName(workfileName)
				};

				return fileContents;
			})
		);
	}

	private getCheckOutJsonContents$() {
		const configHeaders$ = this.workspaceRepository.getConfigHeaders$(this.queryParamsProvider.workfileId);
		const fusionDataTables$ = this.tableRepository.getTables$();
		const columns$ = this.columnRepository.getAllColumns$();
		const workfileName$ = this.workspaceRepository.getWorkFileNameById$(this.queryParamsProvider.workfileId);

		return combineLatest([configHeaders$, fusionDataTables$, columns$, workfileName$]).pipe(
			map(([configHeaders, dataTables, dataColumns, workfileName]) => {
				const fileContents: ExportJSONFileModel = {
					FusionConfigHeader: configHeaders,
					FusionEntities: [],
					FusionDataTables: dataTables,
					FusionDataColumns: dataColumns,
					FusionReferenceTables: [],
					FusionUsers: [],
					TABLE_DATA: [],
					FileName: this.generateFileName(workfileName)
				};

				return fileContents;
			})
		);
	}

	private getQueries(tableInfos: TableInfoModel[]) {
		const dataTableQueries = [];
		const refTableQueries = [];

		for (const table of tableInfos) {
			switch (table.tableType) {
				case TableTypeEnum.RefTable:
					refTableQueries.push(this.refTablesRepository.getAll(table.name.toLowerCase()).pipe(first()));
					break;
				case TableTypeEnum.DataTable:
					dataTableQueries.push(this.dataRepository.getAllTableData$(table.name.toLowerCase()).pipe(first()));
					break;
				default:
					break;
			}
		}
		return { dataTableQueries, refTableQueries };
	}

	private getFusionRefTables$(refTableQueries: any[]) {
		return combineLatest(refTableQueries).pipe(
			map((result: any[]) => {
				const data = [];
				/* eslint-disable */
				for (let index = 0; index < result.length; index++) {
					data.push(...result[index]);
				}
				/* eslint-enable */
				return data;
			})
		);
	}

	private generateFileName(fileName: string): string {
		return fileName + formatDateToString(new Date());
	}

	private isChildRecordOf(primaryKeyValues: ParentRecordPrimaryKeyValuesModel, item: DataModel): boolean {
		// records that match all conditions:
		// 1. project number
		// 2. project value
		// 3. primary key(s) name(s)
		// 4. primary key(s) value(s)
		return (
			primaryKeyValues.projectNumberKey in item &&
			primaryKeyValues.projectNumberValue === item[primaryKeyValues.projectNumberKey] &&
			primaryKeyValues.primaryKeyAndValues.every(
				(pk, i: number) =>
					pk.primaryKey in item &&
					pk.primaryKeyValue === item[primaryKeyValues.primaryKeyAndValues[i].primaryKey]
			)
		);
	}
}
